import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  FileText, Calendar, Truck, DollarSign, Zap, Award,
  ChevronRight, Star, Gift, RefreshCw, CheckCircle, Users,
} from "lucide-react";

// ── Shared design tokens (mirrors Market.jsx) ────────────────────────────────
const DARK   = "#0A0A0A";
const MID    = "#111111";
const CARD   = "#161616";
const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const GREEN  = "#4ADE80";

// ── Tiny helpers ─────────────────────────────────────────────────────────────

// Counter animation hook: counts from 0 → target when element enters view
function useCounterOnView(target, duration = 1800) {
  const ref  = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!seen) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const pct = Math.min((ts - start) / duration, 1);
      // ease-out cubic
      const ease = 1 - Math.pow(1 - pct, 3);
      setVal(Math.floor(ease * target));
      if (pct < 1) requestAnimationFrame(step);
      else setVal(target);
    };
    requestAnimationFrame(step);
  }, [seen, target, duration]);

  return { ref, val };
}
const FADE_UP = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const STAGGER = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.09 } },
};

function GrainOverlay() {
  return (
    <div
      className="fixed inset-0 pointer-events-none z-0 opacity-[0.028]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize: "200px 200px",
      }}
    />
  );
}

function ScanlineBeam() {
  return (
    <motion.div
      initial={{ top: "-10%" }}
      animate={{ top: "110%" }}
      transition={{ duration: 6, repeat: Infinity, ease: "linear", repeatDelay: 4 }}
      className="fixed left-0 right-0 h-px pointer-events-none z-10 opacity-30"
      style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)` }}
    />
  );
}

// ── Step card ────────────────────────────────────────────────────────────────
function StepCard({ step, icon: Icon, color, amount, label, desc, note, tag }) {
  return (
    <motion.div
      variants={FADE_UP}
      className="relative overflow-hidden p-6 flex flex-col gap-3"
      style={{ background: CARD, border: `1px solid rgba(255,255,255,0.07)` }}
    >
      {/* Step number */}
      <div className="absolute top-4 right-5 font-head text-[9px] uppercase tracking-[0.3em] text-white/15">
        {step}
      </div>
      {/* Tag */}
      {tag && (
        <span className="font-head text-[8px] uppercase tracking-[0.15em] px-2 py-0.5 w-fit"
          style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}>
          {tag}
        </span>
      )}
      {/* Icon + amount */}
      <div className="flex items-end gap-3">
        <div className="w-9 h-9 flex items-center justify-center shrink-0"
          style={{ background: `${color}12`, border: `1px solid ${color}30` }}>
          <Icon className="w-4.5 h-4.5" style={{ color }} />
        </div>
        <p className="font-head font-black leading-none"
          style={{ fontSize: "clamp(1.6rem, 5vw, 2.8rem)", color }}>
          {amount}
        </p>
      </div>
      <p className="font-head text-sm font-bold uppercase tracking-[0.06em] text-white">{label}</p>
      <p className="text-xs text-white/45 leading-relaxed">{desc}</p>
      {note && (
        <p className="text-[10px] text-white/25 border-t border-white/06 pt-2 mt-1">{note}</p>
      )}
    </motion.div>
  );
}

// ── GEO / Structured Data ─────────────────────────────────────────────────
const LOYALTY_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": "https://hylascompanion.com/loyalty#program",
      "name": "Hylas Companion Driver Loyalty Program",
      "description": "CDL-A truck drivers earn a minimum of $100 in guaranteed cash bonuses before their first hire at Hylas Companion — $65 for completed documents (MVR, PSP, Clearinghouse), $15 for drug screen date, $20 for confirmed start date. Post-hire carrier plan bonuses add $50–$100+ more.",
      "url": "https://hylascompanion.com/loyalty",
      "provider": {
        "@type": "Organization",
        "name": "Hylas Companion Trucking",
        "url": "https://hylascompanion.com",
      },
      "serviceType": "Driver Recruitment Loyalty Program",
      "areaServed": { "@type": "Country", "name": "United States" },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Driver Loyalty Bonuses",
        "itemListElement": [
          {
            "@type": "Offer",
            "position": 1,
            "name": "Documents Complete Bonus",
            "description": "Upload MVR, PSP, and Clearinghouse record to earn a $65 cash bonus. Admin-approved.",
            "price": "65",
            "priceCurrency": "USD",
          },
          {
            "@type": "Offer",
            "position": 2,
            "name": "Drug Screen Date Set Bonus",
            "description": "Enter your available drug screen date in Driver Hub. $15 earned upon admin confirmation.",
            "price": "15",
            "priceCurrency": "USD",
          },
          {
            "@type": "Offer",
            "position": 3,
            "name": "Start Date Confirmed Bonus",
            "description": "Confirm your earliest start date in Driver Hub. $20 earned upon admin confirmation.",
            "price": "20",
            "priceCurrency": "USD",
          },
          {
            "@type": "Offer",
            "position": 4,
            "name": "Standard Carrier Plan — 50/50 Split",
            "description": "$50 on first day at carrier, $50 after two full weeks. Both payments admin-confirmed.",
            "price": "100",
            "priceCurrency": "USD",
          },
          {
            "@type": "Offer",
            "position": 5,
            "name": "One-Time Carrier Plan Bonus",
            "description": "$75 flat bonus when the carrier selected the one-time discount plan.",
            "price": "75",
            "priceCurrency": "USD",
          },
          {
            "@type": "Offer",
            "position": 6,
            "name": "Referral Bonus — Bring Another Driver",
            "description": "$400 cash bonus for every referred driver who joins the Hylas Market and gets hired by a carrier. No limit on referrals.",
            "price": "400",
            "priceCurrency": "USD",
          },
          {
            "@type": "Offer",
            "position": 7,
            "name": "Mystery Signup Bonus",
            "description": "Additional carrier-specific signup bonus revealed after placement confirmation. Amount varies by carrier.",
            "priceCurrency": "USD",
          },
        ],
      },
    },
    {
      "@type": "WebPage",
      "@id": "https://hylascompanion.com/loyalty",
      "url": "https://hylascompanion.com/loyalty",
      "name": "Driver Loyalty Program — Hylas Companion Trucking",
      "description": "Earn $100+ guaranteed before your first hire. Documents, drug screen, start date — each milestone pays cash. Post-hire carrier plan bonuses add even more.",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", ".loyalty-speakable"],
      },
      "inLanguage": "en-US",
    },
  ],
};

// ── Main export ───────────────────────────────────────────────────────────────
export default function Loyalty() {
  const { ref: counterRef, val: counterVal } = useCounterOnView(100);
  return (
    <motion.div
      key="loyalty-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="min-h-screen relative pt-20" style={{ background: DARK }}>
      <Helmet>
        <title>Driver Loyalty Program — Hylas Companion Trucking</title>
        <meta name="description" content="Earn cash bonuses at every stage of your trucking career with the Hylas Companion Driver Loyalty Program. Documents, drug screen, start date, carrier hire plans, and mystery signup bonuses." />
        <script type="application/ld+json">{JSON.stringify(LOYALTY_SCHEMA)}</script>
      </Helmet>

      <GrainOverlay />
      <ScanlineBeam />

      {/* ── HERO ── */}
      <div
        className="relative overflow-hidden px-6 pt-10 pb-16"
        style={{
          background: `radial-gradient(ellipse 70% 50% at 50% -5%, rgba(255,77,0,0.25) 0%, transparent 60%), #0A0A0A`,
          borderBottom: `1px solid rgba(255,77,0,0.12)`,
        }}
      >
        {/* Grid dots */}
        <div className="absolute inset-0 pointer-events-none opacity-15"
          style={{ backgroundImage: `radial-gradient(circle, rgba(255,77,0,0.12) 1px, transparent 1px)`, backgroundSize: "48px 48px" }} />

        <div className="max-w-[1200px] mx-auto relative">
          <motion.p
            initial={{ opacity: 0, letterSpacing: "1.5em" }} animate={{ opacity: 1, letterSpacing: "0.5em" }}
            transition={{ duration: 1.1 }}
            className="font-head text-[10px] uppercase text-white/25 mb-3">
            Hylas Companion
          </motion.p>

          <div className="overflow-hidden mb-4">
            <motion.h1
              initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-head font-black uppercase leading-none"
              style={{ fontSize: "clamp(3rem, 8vw, 6.5rem)", letterSpacing: "-0.01em" }}>
              <span className="text-white">DRIVER </span>
              <motion.span
                animate={{ textShadow: ["0 0 80px rgba(255,77,0,0.35)", "0 0 120px rgba(255,77,0,0.6)", "0 0 80px rgba(255,77,0,0.35)"] }}
                transition={{ duration: 3.5, repeat: Infinity }}
                style={{ color: ACCENT }}>
                LOYALTY
              </motion.span>
            </motion.h1>
          </div>

          <motion.p
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="font-head text-sm uppercase tracking-[0.3em] text-white/30">
            Every milestone pays. Every hire pays more.
          </motion.p>

          {/* Total potential */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.55 }}
            className="mt-8 inline-flex items-center gap-4 px-6 py-4"
            style={{ background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.25)" }}>
            <DollarSign className="w-5 h-5 shrink-0" style={{ color: GOLD }} />
            <div ref={counterRef}>
              {/* Playfair Display + gold gradient + count-up */}
              <p
                className="leading-none font-black"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                  background: "linear-gradient(135deg, #F5C842 0%, #FFE066 40%, #D4980A 65%, #F5C842 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  letterSpacing: "-0.02em",
                }}
              >
                ${counterVal}+ <span style={{ fontStyle: "italic" }}>guaranteed</span>
              </p>
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-white/35 mt-1">
                before your first hire bonus
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── GEO Citation block — AI-engine quotable fact ── */}
      <figure
        className="loyalty-speakable max-w-[1200px] mx-auto px-6 pt-6"
        itemScope
        itemType="https://schema.org/Quotation"
        data-testid="geo-loyalty-citation"
      >
        <div
          className="flex items-start gap-5 flex-wrap sm:flex-nowrap p-5"
          style={{
            background: "rgba(245,200,66,0.04)",
            borderLeft: `3px solid rgba(245,200,66,0.5)`,
            border: "1px solid rgba(245,200,66,0.14)",
          }}
        >
          <div className="shrink-0">
            <p className="font-head font-black text-2xl leading-none" style={{ color: GOLD }}>$100+</p>
            <p className="font-head text-[8px] uppercase tracking-[0.3em] mt-1" style={{ color: "rgba(245,200,66,0.45)" }}>Guaranteed</p>
          </div>
          <div>
            <p className="font-head text-[8px] uppercase tracking-[0.35em] mb-1.5" style={{ color: "rgba(245,200,66,0.45)" }}>
              Hylas Companion · Pre-Hire Minimum Guarantee
            </p>
            <blockquote itemProp="text" className="text-sm leading-relaxed italic" style={{ color: "rgba(255,255,255,0.55)" }}>
              &ldquo;Every CDL-A driver on the Hylas Market earns a minimum of $100 in guaranteed cash bonuses
              before their first hire — $65 for completed documents (MVR, PSP, Clearinghouse), $15 for a
              confirmed drug screen date, and $20 for a confirmed start date. After hire: $75 one-time plan bonus
              or $100 standard plan. Referral bonus: $400 per referred driver who gets hired. No minimum tenure.
              All bonuses tracked in the Driver Hub and approved by admin.&rdquo;
            </blockquote>
            <figcaption className="mt-2 font-head text-[9px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>
              — Hylas Companion · hylascompanion.com/loyalty
            </figcaption>
          </div>
        </div>
      </figure>

      {/* ── STEP 1: Pre-hire milestones ── */}
      <section className="max-w-[1200px] mx-auto px-6 py-14">
        <motion.div
          variants={STAGGER} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <motion.div variants={FADE_UP} className="mb-8">
            <p className="font-head text-[9px] uppercase tracking-[0.45em] text-white/25 mb-2">Step 1 — Before You're Hired</p>
            <h2 className="font-head font-black text-3xl uppercase tracking-[0.02em] text-white">
              Docs &amp; Readiness Bonuses
            </h2>
            <p className="text-white/40 text-sm mt-2 max-w-xl">
              Submit your paperwork, set your drug screen date, and confirm when you're ready to start.
              Each completed milestone triggers a cash bonus — approved by admin, no surprises.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-5">
            <StepCard
              step="01"
              icon={FileText}
              color={GREEN}
              amount="$65"
              label="Documents Complete"
              desc="Upload your MVR, PSP, and Clearinghouse record. If you have a previous CDL also upload that."
              note="Admin reviews and approves once all 3-4 docs are on file."
              tag="Documents"
            />
            <StepCard
              step="02"
              icon={Calendar}
              color={ACCENT}
              amount="$15"
              label="Drug Screen Date Set"
              desc="Enter the date you're available for a drug screen. This unlocks the $15 bonus in your tracker."
              note="You set the date in your Driver Hub — we confirm it."
              tag="Readiness"
            />
            <StepCard
              step="03"
              icon={Zap}
              color={GOLD}
              amount="$20"
              label="Start Date Confirmed"
              desc="Tell us the earliest date you can begin a new position. Carriers want drivers who are ready to roll."
              note="Setting a date = pending approval. Admin confirms = bonus paid."
              tag="Readiness"
            />
          </div>

          {/* Subtotal bar */}
          <motion.div
            variants={FADE_UP}
            className="mt-6 flex items-center justify-between px-5 py-3"
            style={{ background: "rgba(74,222,128,0.05)", border: "1px solid rgba(74,222,128,0.15)" }}>
            <p className="font-head text-xs uppercase tracking-[0.2em] text-white/40">Step 1 Subtotal</p>
            <p className="font-head font-black text-xl" style={{ color: GREEN }}>$100</p>
          </motion.div>
        </motion.div>
      </section>

      {/* ── DIVIDER ── */}
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-white/07" />
          <div className="flex items-center gap-2 px-4 py-1.5" style={{ background: "rgba(255,77,0,0.08)", border: "1px solid rgba(255,77,0,0.2)" }}>
            <Truck className="w-3.5 h-3.5" style={{ color: ACCENT }} />
            <span className="font-head text-[9px] uppercase tracking-[0.3em] text-white/50">Carrier Hires You</span>
            <ChevronRight className="w-3.5 h-3.5" style={{ color: ACCENT }} />
          </div>
          <div className="flex-1 h-px bg-white/07" />
        </div>
      </div>

      {/* ── STEP 2: After Carrier Hire ── */}
      <section className="max-w-[1200px] mx-auto px-6 py-14">
        <motion.div
          variants={STAGGER} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <motion.div variants={FADE_UP} className="mb-8">
            <p className="font-head text-[9px] uppercase tracking-[0.45em] text-white/25 mb-2">Step 2 — After You're Hired</p>
            <h2 className="font-head font-black text-3xl uppercase tracking-[0.02em] text-white">
              Carrier Plan Bonuses
            </h2>
            <p className="text-white/40 text-sm mt-2 max-w-xl">
              Once a carrier selects you through the Hylas Market, your bonus tracker automatically
              unlocks the bonuses below based on which plan that carrier used. You don't need to do anything —
              they appear in your Driver Hub.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Weekly plan */}
            <StepCard
              step="A"
              icon={RefreshCw}
              color={GOLD}
              amount="10% / week"
              label="Weekly Plan Royalty"
              desc="If your carrier is on the weekly subscription plan, you earn 10% of that weekly fee every week you stay active."
              note="Example: $500/wk plan → you earn $50/wk. Runs for as long as the plan is active."
              tag="Recurring"
            />
            {/* Standard plan */}
            <StepCard
              step="B"
              icon={DollarSign}
              color={ACCENT}
              amount="$50 + $50"
              label="Standard Plan — 50/50 Split"
              desc="Two payments: $50 on your first day, then $50 more after completing two full weeks with the carrier."
              note="$50 at start + $50 at 2-week mark. Both need admin confirmation."
              tag="Two Payments"
            />
            {/* One-time plan */}
            <StepCard
              step="C"
              icon={CheckCircle}
              color={GREEN}
              amount="$75"
              label="One-Time Plan Bonus"
              desc="If the carrier chose the one-time discount plan, you receive a single flat $75 hire bonus — a step up from the standard plan."
              note="One payment. Simple and immediate. Admin confirms."
              tag="One-Time"
            />
          </div>

          {/* Mystery signup */}
          <motion.div
            variants={FADE_UP}
            className="mt-5 relative overflow-hidden p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            style={{ background: `linear-gradient(90deg, rgba(245,200,66,0.06), rgba(255,77,0,0.04))`, border: "1px solid rgba(245,200,66,0.2)" }}>
            {/* shimmer */}
            <motion.div
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "linear", repeatDelay: 3 }}
              className="absolute inset-y-0 w-1/3 pointer-events-none"
              style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)" }}
            />
            <div className="w-12 h-12 flex items-center justify-center shrink-0"
              style={{ background: "rgba(245,200,66,0.12)", border: "1px solid rgba(245,200,66,0.3)" }}>
              <Gift className="w-6 h-6" style={{ color: GOLD }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <p className="font-head font-black text-xl tracking-tight text-white">Mystery Signup Bonus</p>
                <span className="font-head text-[8px] uppercase tracking-[0.15em] px-2 py-0.5"
                  style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.3)" }}>
                  All Plans
                </span>
              </div>
              <p className="text-sm text-white/45 max-w-2xl leading-relaxed">
                Every carrier on the Hylas Market offers their own signup bonus — some carriers go above and beyond.
                The exact amount is revealed to you by the Hylas team after your placement is confirmed.
                This bonus is <span className="text-white/70">on top of</span> your plan bonus above.
              </p>
            </div>
            <p className="font-head font-black text-4xl shrink-0" style={{ color: GOLD }}>???</p>
          </motion.div>
        </motion.div>
      </section>

      {/* ── HOW IT WORKS timeline ── */}
      <section className="max-w-[1200px] mx-auto px-6 pb-14">
        <motion.div
          variants={STAGGER} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <motion.div variants={FADE_UP} className="mb-8">
            <p className="font-head text-[9px] uppercase tracking-[0.45em] text-white/25 mb-2">The Journey</p>
            <h2 className="font-head font-black text-3xl uppercase tracking-[0.02em] text-white">
              How It Works
            </h2>
          </motion.div>

          <div className="space-y-0">
            {[
              { num: "1", color: GREEN,  title: "Join the Market",       desc: "Add your profile in 2 minutes — no account needed. Your name, state, CDL class. Carriers can't see your phone number." },
              { num: "2", color: ACCENT, title: "Complete Your Docs",    desc: "Upload MVR, PSP, Clearinghouse. Set your drug screen and start dates. Earn $100 in tracked bonuses before your first day." },
              { num: "3", color: GOLD,   title: "Carriers Bid on You",   desc: "Carriers compete to hire you by placing bids. Hylas picks the best match — you don't deal with recruiters cold-calling you." },
              { num: "4", color: ACCENT, title: "Get Selected & Hired",  desc: "When a carrier wins the bid, your plan bonuses auto-populate in your tracker. Start date confirmed, bonuses approved, paid out." },
              { num: "5", color: GOLD,   title: "Mystery Bonus Revealed", desc: "Your carrier's signup bonus is announced by the Hylas team and added to your tracker. Some carriers go well above average." },
            ].map(({ num, color, title, desc }) => (
              <motion.div
                key={num}
                variants={FADE_UP}
                className="flex gap-5 pb-6"
              >
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-8 h-8 flex items-center justify-center font-head font-black text-sm"
                    style={{ background: `${color}15`, border: `1px solid ${color}35`, color }}>
                    {num}
                  </div>
                  {num !== "5" && <div className="flex-1 w-px mt-2" style={{ background: "rgba(255,255,255,0.07)" }} />}
                </div>
                <div className="pb-2">
                  <p className="font-head text-sm font-bold uppercase tracking-[0.06em] text-white mb-0.5">{title}</p>
                  <p className="text-xs text-white/40 leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── STEP 3: Referral Bonus ── */}
      <section className="max-w-[1200px] mx-auto px-6 py-14">
        <motion.div
          variants={STAGGER} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <motion.div variants={FADE_UP} className="mb-8">
            <p className="font-head text-[9px] uppercase tracking-[0.45em] text-white/25 mb-2">Step 3 — Grow the Network</p>
            <h2 className="font-head font-black text-3xl uppercase tracking-[0.02em] text-white">
              Referral Bonus
            </h2>
            <p className="text-white/40 text-sm mt-2 max-w-xl">
              Know another CDL-A or box truck driver who's looking? Send them our way.
              Every driver you refer who joins the Hylas Market and gets hired earns you a cash referral bonus.
            </p>
          </motion.div>

          {/* Referral card */}
          <motion.div
            variants={FADE_UP}
            className="relative overflow-hidden p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            style={{ background: `linear-gradient(120deg, rgba(245,200,66,0.07), rgba(255,77,0,0.04))`, border: "1px solid rgba(245,200,66,0.22)" }}>
            {/* Shimmer sweep */}
            <motion.div
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 4 }}
              className="absolute inset-y-0 w-1/3 pointer-events-none"
              style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent)" }}
            />
            <div className="w-16 h-16 flex items-center justify-center shrink-0"
              style={{ background: "rgba(245,200,66,0.1)", border: "1px solid rgba(245,200,66,0.3)" }}>
              <Star className="w-8 h-8" style={{ color: GOLD }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <p className="font-head font-black text-2xl tracking-tight text-white">Refer a Driver · Earn $400</p>
                <span className="font-head text-[8px] uppercase tracking-[0.15em] px-2 py-0.5"
                  style={{ background: "rgba(245,200,66,0.1)", color: GOLD, border: "1px solid rgba(245,200,66,0.3)" }}>
                  Referral
                </span>
              </div>
              <p className="text-sm text-white/50 max-w-2xl leading-relaxed mb-4">
                Bring another driver to Hylas Companion — they join the Market, complete their docs,
                and get hired by a carrier. Once the hire is confirmed, you earn a{" "}
                <span className="text-white/80 font-semibold">$400 referral bonus</span> paid through your Driver Hub tracker.
                No limit on how many drivers you can refer.
              </p>
              <div className="flex flex-wrap gap-4">
                {[
                  "Driver you refer joins the Market",
                  "They complete docs &amp; get hired",
                  "You earn $400 — automatically",
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center font-head font-black text-[9px]"
                      style={{ background: "rgba(245,200,66,0.12)", border: "1px solid rgba(245,200,66,0.3)", color: GOLD }}>
                      {i + 1}
                    </div>
                    <span className="text-xs text-white/40" dangerouslySetInnerHTML={{ __html: step }} />
                  </div>
                ))}
              </div>
            </div>
            <p className="font-head font-black shrink-0" style={{ fontSize: "clamp(2.2rem, 5vw, 3.2rem)", color: GOLD, letterSpacing: "-0.02em" }}>$400</p>
          </motion.div>
        </motion.div>
      </section>

      {/* ── CTA FOOTER ── */}
      <section className="relative overflow-hidden">
        <div
          className="max-w-[1200px] mx-auto px-6 py-12 flex flex-col sm:flex-row items-center gap-8"
          style={{ borderTop: "1px solid rgba(255,77,0,0.15)" }}>
          <div className="flex-1">
            <p className="font-head text-[10px] uppercase tracking-[0.3em] text-white/25 mb-2">Ready?</p>
            <h2 className="font-head font-black text-2xl uppercase tracking-[0.02em] text-white mb-2">Start Earning Today</h2>
            <p className="text-sm text-white/40">Takes 2 minutes to join the Market. All bonuses tracked in your Driver Hub.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/market"
              className="px-7 py-3.5 font-head text-xs font-black uppercase tracking-[0.2em] text-center transition-all hover:brightness-110"
              style={{ background: ACCENT, color: "white" }}>
              Join Market →
            </Link>
            <Link
              to="/apply"
              className="px-7 py-3.5 font-head text-xs font-black uppercase tracking-[0.2em] text-center border transition-all hover:border-white/40"
              style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
              Full Application
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <p className="text-center text-white/15 text-[10px] pb-6 px-4">
        © Hylas Companion Trucking · All bonus amounts subject to admin approval · Market bonuses cannot be combined with outside recruitment offers ·{" "}
        <Link to="/market/terms" className="underline hover:text-white/35 transition-colors">Market Terms</Link>
      </p>
    </motion.div>
  );
}
